import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debug } from '@/utils/debug';

/**
 * Subscription Check Architecture V2 (2025 Best Practice)
 * 
 * INVARIANT: check-subscription may CONFIRM or REVOKE, never GRANT.
 * See: docs/invariants/SUBSCRIPTION_CHECK.md
 * 
 * NO POLLING. Event-driven only:
 * - On login (initial check)
 * - After Stripe checkout return (detected in Timer.tsx)
 * - When tab regains focus after >15 minutes away (industry standard)
 * - Manual refresh via returned function
 * 
 * Combined with Stripe webhooks for real-time DB updates.
 */
export const useSubscriptionCheck = (
  authUser: any,
  currentPremiumStatus: boolean,
  onStatusChange: () => void
) => {
  // Use refs to avoid callback recreation and dependency loops
  const premiumStatusRef = useRef(currentPremiumStatus);
  const authUserRef = useRef(authUser);
  const onStatusChangeRef = useRef(onStatusChange);
  const hasInitialCheckRun = useRef(false);
  const isCheckingRef = useRef(false);
  const lastCheckedRef = useRef(Date.now());
  
  // Update refs without triggering re-renders or callback recreation
  useEffect(() => {
    premiumStatusRef.current = currentPremiumStatus;
  }, [currentPremiumStatus]);
  
  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);
  
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Stable callback that never changes - no dependencies
  const checkSubscriptionStatus = useCallback(async () => {
    // Prevent concurrent checks
    if (isCheckingRef.current) {
      debug.log('[SubscriptionCheck] Check already in progress, skipping');
      return;
    }
    
    if (!authUserRef.current) {
      debug.log('[SubscriptionCheck] No auth user, skipping');
      return;
    }

    isCheckingRef.current = true;

    try {
      // Get session first and validate it exists
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      // If session fetch fails, session is likely expired
      if (sessionError) {
        debug.log('[SubscriptionCheck] Session error, skipping:', sessionError.message);
        return;
      }
      
      const accessToken = sessionData.session?.access_token;
      
      // Don't call edge function if no valid session (e.g., during logout)
      if (!accessToken) {
        debug.log('[SubscriptionCheck] No valid session, skipping');
        return;
      }

      debug.log('[SubscriptionCheck] Checking subscription status...');
      
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Silently handle ALL errors from subscription check
      if (error) {
        debug.log('[SubscriptionCheck] Check failed (expected during logout/session issues)');
        return;
      }

      // Update last checked timestamp on successful check
      lastCheckedRef.current = Date.now();

      const isSubscribed = data?.subscribed === true;
      
      // If subscription status changed, trigger profile reload (via event only, not callback)
      // Using ONLY the event prevents double-triggering in UserContext
      if (isSubscribed !== premiumStatusRef.current) {
        debug.log('[SubscriptionCheck] Status changed from', premiumStatusRef.current, 'to', isSubscribed);
        debug.log('[SubscriptionCheck] Dispatching single profile-updated event');
        window.dispatchEvent(new CustomEvent('profile-updated'));
        // Update ref immediately to prevent duplicate triggers
        premiumStatusRef.current = isSubscribed;
      } else {
        debug.log('[SubscriptionCheck] Status unchanged:', isSubscribed ? 'Premium' : 'Free');
      }
    } catch (error: any) {
      // Silently ignore ALL subscription check errors
      debug.log('[SubscriptionCheck] Check failed silently');
    } finally {
      isCheckingRef.current = false;
    }
  }, []); // Empty dependencies = stable reference

  // Effect 1: Initial check on login (runs once per user, with extended delay after checkout)
  useEffect(() => {
    if (!authUser?.id) {
      hasInitialCheckRun.current = false;
      return;
    }

    // Prevent duplicate initial calls when authUser changes during login
    if (hasInitialCheckRun.current) {
      debug.log('[SubscriptionCheck] Initial check already ran, skipping');
      return;
    }
    
    hasInitialCheckRun.current = true;
    debug.log('[SubscriptionCheck] Running initial check for user:', authUser.id);

    // Check if we're returning from checkout - use longer delay to let webhook process
    const params = new URLSearchParams(window.location.search);
    const isCheckoutReturn = params.get('checkout') === 'success';
    const initialDelay = isCheckoutReturn ? 3000 : 1000;
    
    if (isCheckoutReturn) {
      debug.log('[SubscriptionCheck] Checkout return detected, using extended delay:', initialDelay);
    }

    // Small delay to ensure auth is fully settled
    const initialTimeout = setTimeout(() => {
      checkSubscriptionStatus();
    }, initialDelay);

    return () => {
      clearTimeout(initialTimeout);
      hasInitialCheckRun.current = false;
    };
  }, [authUser?.id, checkSubscriptionStatus]);

  // Effect 2: Tab visibility change - check if away for >15 minutes (industry standard)
  useEffect(() => {
    if (!authUser?.id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Industry standard: 15 minutes cooldown (not 5)
        // See: docs/invariants/SUBSCRIPTION_CHECK.md
        const fifteenMinutes = 15 * 60 * 1000;
        const timeSinceLastCheck = now - lastCheckedRef.current;

        // Only check if user was away for >15 minutes
        if (timeSinceLastCheck > fifteenMinutes) {
          debug.log('[SubscriptionCheck] Tab regained focus after', Math.round(timeSinceLastCheck / 60000), 'min, checking...');
          checkSubscriptionStatus();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authUser?.id, checkSubscriptionStatus]);

  return { checkSubscriptionStatus };
};
